import React, { useState, useMemo, useContext } from 'react';
import { Paper, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import CustomOrderArrow from './IconosOrdenacion';
import MovimentItemEdit from './MovimentItemEdit';
import AppContext from '../AppContext';
import { buildFullTreeWithEntities, resolveMovementConceptAndPath } from '../../services/concepteGraphService';

const MovimentsListEdit = ({ moviments, showYear=false, onError, listConceptes, onEditComplete }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'data', direction: 'desc' });
  const { conceptesTree, entitatsTransversals, conceptesNodesMap } = useContext(AppContext);

  // Función para manejar la ordenación
  const handleSort = (event, key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    event.stopPropagation();
  };

  // Función para ordenar los balances sin mutar el array original (useMemo)
  const sortedBalances = useMemo(() => {
    if (!Array.isArray(moviments)) return [];
    return [...moviments].sort((a, b) => {
      switch (sortConfig.key) {
        case 'moviment':
          return sortConfig.direction === 'asc'
            ? (a.moviment?.moviment || '').localeCompare(b.moviment?.moviment || '')
            : (b.moviment?.moviment || '').localeCompare(a.moviment?.moviment || '');
        case 'import':
          return sortConfig.direction === 'asc'
            ? (a.importe || 0) - (b.importe || 0)
            : (b.importe || 0) - (a.importe || 0);
        case 'data':
          return sortConfig.direction === 'asc'
            ? new Date(a.data) - new Date(b.data)
            : new Date(b.data) - new Date(a.data);
        case 'tipus':
          return sortConfig.direction === 'asc'
            ? (a.moviment?.tipus || '').localeCompare(b.moviment?.tipus || '')
            : (b.moviment?.tipus || '').localeCompare(a.moviment?.tipus || '');
        case 'concepte':
          return sortConfig.direction === 'asc'
            ? (a.concepte || '').localeCompare(b.concepte || '')
            : (b.concepte || '').localeCompare(a.concepte || '');
        default:
          return 0;
      }
    });
  }, [moviments, sortConfig]);

  // Construir l'arbre enriquit un sol cop per a tota la llista (evita clonar JSON 333 vegades)
  const enrichedTree = useMemo(() => {
    return buildFullTreeWithEntities(conceptesTree, entitatsTransversals);
  }, [conceptesTree, entitatsTransversals]);

  // Pre-indexar O(1) les metadades de ruta i avís pare per als conceptes únics
  const getCacheKey = (m) => {
    const idFulla = m.nodeFulla?.id || m.id_node_fulla || m.idNodeFulla || (typeof m.nodeFulla === 'number' ? m.nodeFulla : null);
    const idN1 = m.nodeN1?.id || m.id_node_n1 || m.idNodeN1 || (typeof m.nodeN1 === 'number' ? m.nodeN1 : null);
    const classic = m.concepte || '';
    return `${idN1 || ''}::${idFulla || ''}::${classic}`;
  };

  const pathInfoMap = useMemo(() => {
    const map = new Map();
    if (!sortedBalances || !enrichedTree) return map;

    sortedBalances.forEach(m => {
      const cacheKey = getCacheKey(m);
      if (!map.has(cacheKey)) {
        const resolved = resolveMovementConceptAndPath(m, enrichedTree, conceptesNodesMap);
        map.set(cacheKey, resolved);
      }
    });

    return map;
  }, [sortedBalances, enrichedTree, conceptesNodesMap]);
  
  return (
    <Paper sx={{ padding: '20px' }}>
      
      {/* Cabecera del Grid */}
      <Grid container spacing={2} alignItems="center" 
        sx={{ 
          marginBottom: 2, 
          backgroundColor: '#f0f0f0', // Fondo claro
          color: '#000', // Texto oscuro
          paddingTop: '2px', 
          paddingBottom: '2px',
          paddingLeft: '10px',
          paddingRight: '10px',
          borderBottom: '2px solid #ddd' // Borde inferior
        }}>
        <Grid size={{xs: 12, sm: 1 }}>
          <Typography variant="small" onClick={(e) => handleSort(e, 'data')}>
              Data {sortConfig.key === 'data' && <CustomOrderArrow direction={sortConfig.direction}/> }
          </Typography>
        </Grid>
        <Grid size={{xs: 12, sm: 0.5 }} container direction="column" 
          onClick={(e) => handleSort(e, 'tipus')}>
          <Typography variant="small">
              Tipus {sortConfig.key === 'tipus' && <CustomOrderArrow direction={sortConfig.direction}/> }
          </Typography>
        </Grid>
        <Grid size={{xs: 12, sm: 2 }} container direction="column" 
          onClick={(e) => handleSort(e, 'concepte')}>
          <Typography variant="small">
              Concepte {sortConfig.key === 'concepte' && <CustomOrderArrow direction={sortConfig.direction}/> }
          </Typography>
        </Grid>
        <Grid size={{xs: 12, sm: 4 }} container direction="column" 
          onClick={(e) => handleSort(e, 'moviment')}
          sx={{ gap: '1px' }}>
          <Typography variant="small" >
            Moviment {sortConfig.key === 'moviment' && <CustomOrderArrow direction={sortConfig.direction}/> }
          </Typography>
        </Grid>
        <Grid size={{xs: 12, sm: 2 }} container direction="column" 
          sx={{ gap: '1px' }}>
          <Typography variant="small" >
            Descripció
          </Typography>
        </Grid>
        <Grid size={{xs: 12, sm: 1.5 }} container direction="column" alignItems="flex-end" 
          onClick={(e) => handleSort(e, 'import')}
          sx={{ gap: '1px' }}>
          <Typography variant="small" >
            Import {sortConfig.key === 'import' && <CustomOrderArrow direction={sortConfig.direction}/> }
          </Typography>
        </Grid>
        <Grid size={{xs: 12, sm: 1 }} container direction="column" 
          sx={{ gap: '1px' }}>
          <Typography variant="small" >
          </Typography>
        </Grid>
      </Grid>    
      
      {/* List Movs */}
      { sortedBalances.map((value) => {
        const cacheKey = getCacheKey(value);
        const pathInfo = pathInfoMap.get(cacheKey);

        return (
          <MovimentItemEdit 
            key={value.id} 
            movementData={value} 
            showYear={showYear} 
            onError={onError} 
            listConceptes={listConceptes} 
            onEditComplete={onEditComplete}
            pathInfo={pathInfo}
            enrichedTree={enrichedTree}
          />
        );
      }) }
    </Paper>
  );
};

export default MovimentsListEdit;
